import { useState, useCallback, useRef, useEffect } from "react";

export interface UseCaptureCountdownOptions {
  /** Countdown duration in seconds (e.g. 5 for 5,4,3,2,1). */
  duration?: number;
  /** Called when countdown reaches 0. */
  onComplete: () => void;
  /** Called when countdown is cancelled. */
  onCancel?: () => void;
}

export function useCaptureCountdown(options: UseCaptureCountdownOptions) {
  const { duration = 5, onComplete, onCancel } = options;
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onCancelRef = useRef(onCancel);
  onCompleteRef.current = onComplete;
  onCancelRef.current = onCancel;

  const start = useCallback(() => {
    setSecondsLeft(duration);
  }, [duration]);

  const cancel = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setSecondsLeft(null);
    onCancelRef.current?.();
  }, []);

  useEffect(() => {
    if (secondsLeft === null) return;
    if (secondsLeft <= 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setSecondsLeft(null);
      onCompleteRef.current();
      return;
    }
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s === null || s <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          onCompleteRef.current();
          return null;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [secondsLeft]);

  return { secondsLeft, start, cancel, isActive: secondsLeft !== null };
}
